import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import jobListingsService from '../../services/JobListingsService';
import assessmentService, { inviteService, buildAssessmentLink } from '../../services/AssessmentService';
import { generateAccessToken } from '../../utils/accessToken';
import { contactKeysFromAnswers } from '../../utils/applicantIdentity';
import {
  composeInvitationBody,
  buildMailtoUrl,
  hasOpenPlaceholders,
  firstNameOf,
} from '../../utils/invitationEmail';
import { rankJobListings, pickPreselection, normalizeJobText } from '../../utils/jobMatching';
import { withMarkedPlaceholders } from './placeholderPreview';

/**
 * Fragebogen 2 aus einer Bewerbung heraus verschicken.
 *
 * Zwei Schritte, weil zwei Fragen zu beantworten sind: an welche Stelle, und
 * geht der Text so raus. Beide auf einmal wären vier Entscheidungen gleichzeitig.
 *
 * Der Dialog kennt nur eine Bewerbung. Was mit der Ergebnisseite passiert,
 * geht ihn nichts an — er meldet über onSent, dass etwas rausging.
 */
export function SendAssessmentDialog({ submission, onClose, onSent, onSetUpJob }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobIdsWithAssessment, setJobIdsWithAssessment] = useState(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState('job');
  const [assessment, setAssessment] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [firstName, setFirstName] = useState(() => firstNameOf(submission.applicantName));
  const [email, setEmail] = useState(() => contactKeysFromAnswers(submission.answers).emails[0] || '');
  const [sending, setSending] = useState(false);
  const [editingBody, setEditingBody] = useState(false);
  // null = untouched: the mail is whatever composeInvitationBody produced.
  // Set only once HR actually types into the textarea — merely opening the
  // edit view must not freeze the text against a later job/name change.
  const [editedBody, setEditedBody] = useState(null);

  // Der Token entsteht, bevor irgendetwas gespeichert ist: nur so kann die
  // Vorschau den Link zeigen, der später wirklich in der Mail steht. Je
  // Dialog einer — ein zweiter Versuch soll denselben Link behalten.
  const [token] = useState(() => generateAccessToken());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [listing, withAssessment] = await Promise.all([
        jobListingsService.getJobListings(),
        assessmentService.getJobIdsWithAssessment(),
      ]);
      if (cancelled) return;
      const loadedJobs = listing.success ? listing.data : [];
      const ids = withAssessment.success ? withAssessment.data : new Set();
      setJobs(loadedJobs);
      setJobIdsWithAssessment(ids);
      setSelectedJob(pickPreselection(rankJobListings(submission.jobTitle, loadedJobs), ids));
      // Ein leeres Ergebnis und ein fehlgeschlagener Aufruf sehen sonst gleich
      // aus — hier wird gesagt, welches der beiden Fälle es war.
      const problems = [];
      if (!listing.success) {
        problems.push(listing.error || 'Die Stellenanzeigen konnten nicht geladen werden.');
      }
      if (!withAssessment.success) {
        problems.push(
          withAssessment.error ||
            'Es ließ sich nicht prüfen, für welche Stellen ein Fragebogen 2 hinterlegt ist.'
        );
      }
      setError(problems.length > 0 ? problems.join(' ') : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [submission.jobTitle]);

  const ranked = useMemo(
    () => rankJobListings(submission.jobTitle, jobs),
    [submission.jobTitle, jobs]
  );
  const matched = useMemo(() => ranked.filter((entry) => entry.match !== 'none'), [ranked]);

  // Ohne Treffer hat "nur die passenden" nichts zu zeigen — dann ist die volle
  // Liste die einzig sinnvolle Ansicht, ohne dass jemand erst klicken muss.
  const listAll = showAll || matched.length === 0;
  const visible = useMemo(() => {
    const base = listAll ? ranked : matched;
    const needle = normalizeJobText(search);
    const filtered = needle
      ? base.filter((entry) =>
          normalizeJobText(`${entry.job.title} ${entry.job.role || ''}`).includes(needle)
        )
      : base;
    // Die gewählte Stelle bleibt sichtbar, was Suche und Klappliste auch tun.
    // Sonst stünde "Weiter" für etwas bereit, das niemand mehr sieht.
    if (!selectedJob || filtered.some((entry) => entry.job.id === selectedJob.id)) return filtered;
    const selected = ranked.find((entry) => entry.job.id === selectedJob.id);
    return selected ? [selected, ...filtered] : filtered;
  }, [listAll, ranked, matched, search, selectedJob]);

  const hasQuestionnaire = useCallback(
    (job) => jobIdsWithAssessment.has(String(job.id)),
    [jobIdsWithAssessment]
  );

  // Genau der Text, der gleich verschickt wird — dieselbe Funktion, kein
  // zweiter Weg, der irgendwann auseinanderläuft.
  const mailBody = useMemo(
    () =>
      assessment
        ? composeInvitationBody({
            body: assessment.emailBody,
            link: buildAssessmentLink(assessment.id, token),
            firstName: firstName.trim(),
            campaignUrl: selectedJob?.campaignUrl,
          })
        : '',
    [assessment, token, firstName, selectedJob]
  );
  // Der Text, der wirklich rausgeht: die Bearbeitung, sobald es eine gibt,
  // sonst der zusammengesetzte Text. Genau dieser eine Wert geht in Vorschau,
  // Warnung und Versand — kein zweiter Weg, der auseinanderlaufen könnte.
  const outgoingBody = editedBody ?? mailBody;
  // Betreff und Text gehen beide unverändert raus, also zählt für die Warnung
  // ein Platzhalter in beiden — nicht nur im Text.
  const openPlaceholders = hasOpenPlaceholders(`${assessment?.emailSubject || ''}\n${outgoingBody}`);

  async function goToMail() {
    if (!selectedJob) return;
    setLoadingAssessment(true);
    const result = await assessmentService.getByJobId(selectedJob.id);
    setLoadingAssessment(false);
    if (!result.success || !result.data) {
      toast.error(result.error || 'Für diese Stelle ist kein Fragebogen 2 hinterlegt.');
      return;
    }
    setAssessment(result.data);
    setStep('mail');
  }

  async function handleSend() {
    setSending(true);
    const result = await inviteService.create({
      assessment,
      email: email.trim(),
      applicantId: submission.id,
      firstName: firstName.trim(),
      token,
    });
    setSending(false);
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    const mail = buildMailtoUrl({ to: email.trim(), subject: assessment.emailSubject, body: outgoingBody });
    window.location.href = mail.url;
    toast.success(
      mail.mayBeTruncated ? 'Mail geöffnet. Bitte prüfen, ob der Text vollständig ist.' : 'Mail geöffnet'
    );
    // Non-fatal: the mail is already open. Only the "sent" bookkeeping would
    // be missing if this fails (e.g. a schema not yet provisioned for it) —
    // markSent itself never throws, it reports failure instead.
    inviteService.markSent(result.data.id).then((sentResult) => {
      if (!sentResult.success) console.warn('markSent failed:', sentResult.error);
    });
    onSent?.(result.data);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Fragebogen 2 senden</h2>
            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
              {submission.applicantName || 'Ohne Namen'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Wird geladen …</p>
          ) : (
            <>
              {error && (
                <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {step === 'job' && (
                // Der breitere Dialog dient Schritt 2 (Vorschau/Text); eine
                // Liste kurzer Zeilen über die volle Breite wirkt sonst leer.
                <div className="mx-auto max-w-xl space-y-4">
                  <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 px-4 py-3">
                    {submission.jobTitle ? (
                      <>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Bewerber hat angegeben:</p>
                        <p className="text-sm text-slate-800 dark:text-slate-200">„{submission.jobTitle}“</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Keine Stelle erkannt — die Bewerbung nennt keine. Bitte unten auswählen.
                      </p>
                    )}
                  </div>

                  {listAll && (
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Stelle suchen …"
                      aria-label="Stelle suchen"
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}

                  <ul role="radiogroup" className="space-y-1.5">
                    {visible.map(({ job, match }) => {
                      const selectable = hasQuestionnaire(job);
                      const selected = selectedJob?.id === job.id;
                      return (
                        <li key={job.id} role="presentation">
                          <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            data-job-id={job.id}
                            disabled={!selectable}
                            onClick={() => selectable && setSelectedJob(job)}
                            className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                              selected
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                            } ${selectable ? '' : 'opacity-60 cursor-not-allowed'}`}
                          >
                            <span className="block truncate text-sm text-slate-800 dark:text-slate-200">{job.title}</span>
                            {job.role && (
                              <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{job.role}</span>
                            )}
                            {selectable && (
                              <span className="mt-0.5 block text-xs text-emerald-700 dark:text-emerald-400">
                                Fragebogen 2 hinterlegt
                                {match === 'exact' && ' · aus der Bewerbung erkannt'}
                                {match === 'partial' && ' · Vorschlag'}
                              </span>
                            )}
                          </button>
                          {/* Außerhalb des Buttons: ein Button im Button ist ungültiges HTML,
                              und ein deaktivierter Button schluckt ohnehin jeden Klick darin. */}
                          {!selectable && (
                            <p className="mt-0.5 px-1 text-xs text-amber-700 dark:text-amber-400">
                              kein Fragebogen 2 hinterlegt —{' '}
                              <button
                                type="button"
                                onClick={() => onSetUpJob?.()}
                                className="font-medium underline hover:no-underline"
                              >
                                unter Stellenanzeigen einrichten
                              </button>
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {!listAll && (
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      className="text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    >
                      Alle {ranked.length} Stellen anzeigen
                    </button>
                  )}

                  <div className="flex justify-end pt-2">
                    <ModernButton onClick={goToMail} disabled={!selectedJob || loadingAssessment} loading={loadingAssessment}>
                      Weiter
                    </ModernButton>
                  </div>
                </div>
              )}

              {step === 'mail' && assessment && (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{selectedJob.title}</p>

                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Vorname"
                      className="sm:w-40 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="bewerber@example.de"
                      className="flex-1 min-w-56 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Betreff: <span className="text-slate-700 dark:text-slate-200">{withMarkedPlaceholders(assessment.emailSubject)}</span>
                  </p>

                  <div>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Das geht raus:</p>
                      <button
                        type="button"
                        onClick={() => setEditingBody((v) => !v)}
                        className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {editingBody ? 'Vorschau anzeigen' : 'Text anpassen'}
                      </button>
                    </div>

                    {editingBody ? (
                      <>
                        <textarea
                          data-testid="mail-body-editor"
                          value={outgoingBody}
                          onChange={(e) => setEditedBody(e.target.value)}
                          className="max-h-96 min-h-96 w-full overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-4 py-3 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                          Gilt nur für diese Mail — die Vorlage unter Stellenanzeigen bleibt unverändert.
                        </p>
                        {editedBody !== null && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Dein bearbeiteter Text bleibt stehen.{' '}
                            <button
                              type="button"
                              onClick={() => setEditedBody(null)}
                              className="font-medium underline hover:no-underline"
                            >
                              Zurücksetzen
                            </button>
                          </p>
                        )}
                      </>
                    ) : (
                      <div
                        data-testid="mail-preview"
                        className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 text-xs text-slate-700 dark:text-slate-200"
                      >
                        {withMarkedPlaceholders(outgoingBody)}
                      </div>
                    )}
                  </div>

                  {openPlaceholders && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      In der Mail steht noch ein Platzhalter in geschweiften Klammern. So würde er
                      mitverschickt.
                    </p>
                  )}

                  <div className="flex justify-between gap-2 pt-2">
                    <ModernButton variant="secondary" onClick={() => setStep('job')}>
                      Zurück
                    </ModernButton>
                    <ModernButton onClick={handleSend} disabled={!email.trim() || sending} loading={sending}>
                      E-Mail-Programm öffnen
                    </ModernButton>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
